import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleBySlug, getRelatedArticles, getLatestArticles } from '@/mocks/articles';
import { ArticleSchema, BreadcrumbListSchema } from '@/components/feature/SEOSchema';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const relatedArticles = article ? getRelatedArticles(article.slug, article.categorySlug) : [];
  const latestArticles = getLatestArticles(5);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 17吃瓜网`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', article.excerpt);
      }
      const metaKw = document.querySelector('meta[name="keywords"]');
      if (metaKw) {
        metaKw.setAttribute('content', `17吃瓜网,${article.tags.join(',')},娱乐八卦,吃瓜爆料`);
      }
    }
  }, [article]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mt-4">文章未找到</h1>
            <p className="text-foreground-600 mt-2">该文章可能已被删除或不存在</p>
            <Link to="/" className="inline-block mt-6 px-6 py-3 rounded-lg bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const siteUrl = 'https://17chigua.com';
  const articleUrl = `${siteUrl}/article/${article.slug}`;

  return (
    <div className="min-h-screen bg-background-50">
      <ArticleSchema
        url={articleUrl}
        title={article.title}
        description={article.excerpt}
        image={article.image}
        datePublished={article.publishDate}
        dateModified={article.publishDate}
        authorName={article.author}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', item: siteUrl },
          { name: article.categoryName, item: `${siteUrl}/category/${article.categorySlug}` },
          { name: article.title },
        ]}
      />
      <Navbar />
      <div className="pt-16">
        <Breadcrumb />

        <article className="py-8 md:py-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="mb-6">
                  <Link to={`/category/${article.categorySlug}`} className="inline-block w-fit mb-3">
                    <span className="text-xs font-medium text-primary-500 bg-primary-50 px-3 py-1 rounded-full">{article.categoryName}</span>
                  </Link>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 leading-tight">{article.title}</h1>
                  <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-600">
                    <span className="flex items-center gap-1"><i className="ri-user-line"></i> {article.author}</span>
                    <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {article.publishDate}</span>
                    <span className="flex items-center gap-1"><i className="ri-eye-line"></i> {article.views.toLocaleString()} 阅读</span>
                  </div>
                </div>

                <div className="w-full aspect-video rounded-xl overflow-hidden bg-background-200 mb-8">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={`${article.title} - 17吃瓜网`}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div
                  className="prose prose-lg max-w-none text-foreground-800 leading-relaxed article-content"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1.5 rounded-full text-xs font-medium bg-secondary-50 text-secondary-700">
                      #{tag}
                    </span>
                  ))}
                </div>

                {relatedArticles.length > 0 && (
                  <div className="mt-10 pt-8 border-t border-background-200">
                    <div className="flex items-center gap-2 mb-5">
                      <div className="w-1 h-5 rounded-full bg-primary-500"></div>
                      <h2 className="font-heading text-lg font-bold text-foreground-900">相关文章</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      {relatedArticles.map((rel) => (
                        <ArticleCard
                          key={rel.id}
                          slug={rel.slug}
                          title={rel.title}
                          excerpt={rel.excerpt}
                          image={rel.image}
                          categoryName={rel.categoryName}
                          categorySlug={rel.categorySlug}
                          publishDate={rel.publishDate}
                          views={rel.views}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <aside className="lg:col-span-1">
                <div className="sticky top-24 space-y-6">
                  <div className="bg-background-100 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-1 h-4 rounded-full bg-accent-500"></div>
                      <h3 className="font-heading font-semibold text-foreground-900">最新资讯</h3>
                    </div>
                    <div className="flex flex-col gap-3">
                      {latestArticles.map((art) => (
                        <hr key={`divider-${art.id}`} className="hidden" />
                      ))}
                      {latestArticles.map((art, idx) => (
                        <div key={art.id}>
                          {idx > 0 && <div className="border-t border-background-200 mb-3"></div>}
                          <Link
                            to={`/article/${art.slug}`}
                            className="group cursor-pointer"
                          >
                            <div className="flex gap-3">
                              <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
                                <img src={art.image} alt={art.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">{art.title}</h4>
                                <span className="text-xs text-foreground-500 mt-1 block">{art.publishDate}</span>
                              </div>
                            </div>
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-accent-500 to-accent-700 rounded-xl p-5 text-white">
                    <h3 className="font-heading font-bold text-lg mb-2">📢 爆料有奖</h3>
                    <p className="text-sm text-white/80 leading-relaxed mb-4">你有独家瓜料？欢迎来爆料，采用即付稿酬！</p>
                    <a
                      href="mailto:tips@17chigua.com"
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-medium transition-colors cursor-pointer"
                    >
                      <i className="ri-mail-send-line"></i> 立即爆料
                    </a>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </article>
      </div>
      <Footer />
    </div>
  );
}